import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import {
    Colours,
    Sizes,
    RemoveDefaultButtonStyles,
    IconChevronDown,
    MessagePropType,
    toKebabCase,
    replaceNonAlphanumeric,
    noticeError,
} from '@ratehub/base-ui';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import BusinessUnitsMessages from '../definitions/BusinessUnitsMessages';
import { RatePropType } from '../definitions/BestMortgageRatesPropType';
import HomeInsuranceProviderRedirectsPropType from '../definitions/HomeInsuranceProviderRedirectsPropType';
import useResizeObserver from '../hooks/useResizeObserver';
import HeroTabContentMortgages from './HeroTabContentMortgages';
import HeroTabContentInsurance from './HeroTabContentInsurance';
import HeroTabContentCreditCards from './HeroTabContentCreditCards';
import HeroTabContentLoans from './HeroTabContentLoans';
import HeroTabContentBanking from './HeroTabContentBanking';
import HeroTabContentInvesting from './HeroTabContentInvesting';


function HeroTabContainer({
    className,
    initialTabIndex = 0,

    initialMortgageRates = [],
    homeInsuranceProviderRedirects = {},

    ...otherProps
}) {
    const intl = useIntl();

    const tabListRef = useRef(null);
    const tabRefs = useRef([]);

    const [ size, setRef ] = useResizeObserver();

    const [ activeIndex, setActiveIndex ] = useState(initialTabIndex);
    const [ isOverflowTabVisible, setIsOverflowTabVisible ] = useState(false);
    const [ isOverflowMenuVisible, setIsOverflowMenuVisible ] = useState(false);
    const [ isTouch, setIsTouch ] = useState(true);

    useEffect(() => {
        setIsTouch(isTouchDevice());
    }, []);

    const containerWidth = size?.width;
    const firstTabIndex = 0;
    const lastTabIndex = TABS.length - 1;

    function handleTabClick(index) {
        setActiveIndex(index);
        if (isOverflowMenuVisible) {
            setIsOverflowMenuVisible(false);
        }
    }

    function handleTabKeyDown(e) {
        // Don't allow user to arrow beyond first/last tab.
        const nextIndex = Math.min(activeIndex + 1, lastTabIndex);
        const prevIndex = Math.max(activeIndex - 1, firstTabIndex);

        // Only actually set state when the activeIndex is truly going to change.
        switch (e.key) {
            case 'ArrowRight':
                if (nextIndex !== activeIndex) {
                    setActiveIndex(nextIndex);
                }
                break;
            case 'ArrowLeft':
                if (prevIndex !== activeIndex) {
                    setActiveIndex(prevIndex);
                }
                break;
        }
    }

    function handleOverflowButtonClick() {
        setIsOverflowMenuVisible(!isOverflowMenuVisible);
    }

    function handleOverflowMenuItemClick(index) {
        // Scroll the selected tab into view.
        if (tabRefs?.current?.[index]) {
            tabRefs.current[index].scrollIntoView({ behavior: 'smooth', inline: 'end' });
        }

        setActiveIndex(index);
        setIsOverflowMenuVisible(false);
    }

    // When the container width changes, determine if overflow tab should show
    useEffect(() => {
        if (!tabListRef.current) {
            noticeError(new Error('[HeroTabContainer] Tab list ref is not assigned to an element'));
            return;
        }

        const element = tabListRef.current;

        const observer = new ResizeObserver(entries => {
            const totalWidth = entries[0].target.scrollWidth;
            setIsOverflowTabVisible(!isTouch && containerWidth < totalWidth);
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [ containerWidth, isTouch ]);

    return (
        <Container
            className={className}
            ref={setRef}
            {...otherProps}
        >
            <div className="tab-container rh-display-flex">
                <ul
                    ref={tabListRef}
                    className={classNames(
                        'tab-list rh-flex-grow-1 rh-list-style-type-none rh-display-flex rh-position-relative',
                        'rh-m-0 rh-p-0',
                        {
                            'rh-overflow-hidden': isTouch === false,
                            'rh-overflow-scroll': isTouch === true,
                        }
                    )}

                    // "tablist" is 100% the correct way to semantically indicate that a <ul> contains
                    // a list of tabs. Don't let our linter tell you otherwise, the rule only exists
                    // to stop people from assigning roles to wildly innapproiapriate elements, like
                    // saying a heading is tablist for example.
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                    role="tablist"

                    onKeyDown={handleTabKeyDown}
                >
                    {/* Main tabs */}
                    <For
                        each="tab"
                        of={TABS}
                        index="index"
                    >
                        <With
                            title={intl.formatMessage(tab.title, intl)}
                            keySuffix={replaceNonAlphanumeric(toKebabCase(intl.formatMessage(tab.title, intl)))}
                        >
                            <li
                                key={`tab-list-item-${keySuffix}`}
                                className={classNames(
                                    'tab rh-flex-grow-1 rh-border-style-solid rh-border-color-blueberry',
                                    'rh-text-align-center rh-mb-0',
                                    {
                                        'active rh-bg-coconut': index === activeIndex,
                                        'rh-bg-grey-200': index !== activeIndex,
                                    },
                                )}
                                ref={element => {
                                    tabRefs.current[index] = element;
                                }}
                            >
                                <button
                                    className={classNames(
                                        'tab-button rh-display-block rh-width-100p rh-text-m weight-medium ',
                                        'rh-m-0 rh-text-wrap-nowrap rh-text-uppercase rh-no-wrap',
                                    )}
                                    onClick={() => handleTabClick(index)}
                                    type="button"
                                    role="tab"
                                    aria-selected={index === activeIndex}
                                    data-name={`tab-button-${keySuffix}`}
                                >
                                    {title}
                                </button>
                            </li>
                        </With>
                    </For>
                </ul>

                {/* Overflow tab */}
                <If condition={isOverflowTabVisible}>
                    <div
                        className={classNames(
                            'tab active overflow-tab rh-bg-coconut',
                            'rh-border-style-solid rh-border-color-blueberry',
                        )}
                    >
                        {/* TODO: Needs aria roles? */}
                        <button
                            className={classNames(
                                'overflow-button rh-display-flex rh-justify-content-center rh-align-items-center',
                                'rh-height-100p',
                            )}
                            type="button"
                            onClick={handleOverflowButtonClick}
                        >
                            <IconChevronDown
                                className={classNames(
                                    'chevron-icon rh-px-0_5 rh-position-relative',
                                    {
                                        'is-inverse': isOverflowMenuVisible,
                                    }
                                )}
                                outlineWidth="0px"
                                strokeWidth="3px"
                                stroke={Colours.BLACKBERRY}
                                width={Sizes.ICONS.M}
                            />
                        </button>

                        <OverflowMenu
                            isVisible={isOverflowMenuVisible}
                            tabs={TABS}
                            onMenuItemClick={handleOverflowMenuItemClick}
                        />
                    </div>
                </If>
            </div>

            {/* Content panels */}
            <ul className="tab-panels rh-list-style-type-none rh-m-0 rh-p-0">
                <For
                    each="tab"
                    of={TABS}
                    index="index"
                >
                    <div
                        key={`tab-panel-${replaceNonAlphanumeric(
                            toKebabCase(intl.formatMessage(tab.title, intl))
                        )}`}
                        className={classNames(
                            'panel rh-align-items-stretch rh-p-2',
                            'rh-bg-coconut rh-border-style-solid rh-border-color-blueberry',
                            {
                                'rh-display-none': index !== activeIndex,
                                'rh-display-grid': index === activeIndex,
                            },
                        )}
                        role="tabpanel"
                    >
                        {tab.render({
                            initialMortgageRates,
                            homeInsuranceProviderRedirects,
                        })}
                    </div>
                </For>
            </ul>
        </Container>
    );
}

HeroTabContainer.propTypes = {
    className: PropTypes.string,
    initialTabIndex: PropTypes.number,

    initialMortgageRates: PropTypes.arrayOf(RatePropType),
    homeInsuranceProviderRedirects: HomeInsuranceProviderRedirectsPropType.isRequired,
};

const OVERFLOW_MENU_TRANSITION_DURATION = '250ms';

const SharedStyles = css`
    --border-radius: 8px;

    // Setting border styles individually with our utility classes
    // is a total pain, need to look into improving this, we do this quite often.
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
`;

const Container = styled.div`
    > .tab-container {
        > .tab-list {
            gap: 1px;

            > .tab {
                ${SharedStyles};

                border-width: 1px 1px 0;
                border-color: transparent;
                margin-bottom: 1px;

                &.active {
                    margin-bottom: 0;
                    border-color: ${Colours.BLUEBERRY};
                }

                > .tab-button {
                    ${RemoveDefaultButtonStyles};

                    // Overrides padding in RemoveDefaultButtonStyles
                    padding: ${Sizes.SPACING.ONE} ${Sizes.SPACING.TWO};
                    &:active,
                    &:focus {
                        color: ${Colours.BLUEBERRY_DARK};
                    }
                }
            }
        }

        > .overflow-tab {
            ${SharedStyles};

            border-width: 1px 1px 0;

            > .overflow-button {
                ${RemoveDefaultButtonStyles};

                // Overrides padding in RemoveDefaultButtonStyles
                padding: 0;
                &:active,
                &:focus {
                    color: ${Colours.BLUEBERRY_DARK};
                }

                > .chevron-icon {
                    /* Vertically align the label & the icon */
                    top: -.1rem;
                    transition: ${OVERFLOW_MENU_TRANSITION_DURATION} transform ease-out;

                    &.is-inverse {
                        transform: rotate(180deg);
                    }
                }
            }
        }
    }

    > .tab-panels {
        > .panel {
            min-height: 31rem; // Based off visual height of panel with most content
            border-width: 1px;
            margin-top: -1px; // To collapse the border with the active tab's bottom border

            border-bottom-left-radius: var(--border-radius);
            border-bottom-right-radius: var(--border-radius);
        }
    }
`;

function OverflowMenu({ isVisible = false, tabs, onMenuItemClick }) {
    const intl = useIntl();

    return (
        <OverflowMenuContainer
            className={classNames(
                'rh-bg-coconut rh-box-shadow-surround rh-border-width-1px rh-border-style-solid',
                'rh-border-color-blueberry rh-border-radius-4px rh-position-absolute rh-right-0',
                'rh-mt-1 rh-mr-1 rh-p-2 rh-zindex-modals',
                {
                    'is-visible': isVisible,
                }
            )}
        >
            <h3
                className="rh-text-m weight-medium rh-text-uppercase rh-fg-blueberry-darkest rh-mt-0 rh-mx-0 rh-mb-0_5 rh-no-wrap"
            >
                <FormattedMessage
                    id="web-components.hero-tab-container.overflow-menu-header"
                    defaultMessage="All categories"
                />
            </h3>
            <ul
                className="overflow-menu-list rh-list-style-type-none rh-m-0 rh-p-0 rh-text-align-left"
            >
                <For
                    each="tab"
                    of={tabs}
                    index="index"
                >
                    <With
                        title={intl.formatMessage(tab.title, intl)}
                        keySuffix={replaceNonAlphanumeric(toKebabCase(intl.formatMessage(tab.title, intl)))}
                    >
                        <li
                            key={`overflow-menu-item-${keySuffix}`}
                            className="overflow-menu-item"
                        >
                            <button
                                onClick={() => onMenuItemClick?.(index)}
                                className={classNames(
                                    'overflow-menu-button rh-no-wrap rh-text-m focus--rh-fg-blueberry-darkest',
                                    'rh-text-uppercase weight-medium rh-my-0_5',
                                )}
                                type="button"
                                data-name={`overflow-menu-item-${keySuffix}`}
                            >
                                {title}
                            </button>
                        </li>
                    </With>
                </For>
            </ul>
        </OverflowMenuContainer>
    );
}

OverflowMenu.propTypes = {
    isVisible: PropTypes.bool,
    tabs: PropTypes.arrayOf(PropTypes.shape({
        title: MessagePropType,
    })),
    onMenuItemClick: PropTypes.func.isRequired,
};

const OverflowMenuContainer = styled.div`
    width: auto;
    transition: ${OVERFLOW_MENU_TRANSITION_DURATION} opacity ease-out;
    opacity: 0;
    pointer-events: none;

    &.is-visible {
        opacity: 1;
        pointer-events: auto;
    }

    > .overflow-menu-list {
        > .overflow-menu-item {
            &:last-child {
                > .overflow-menu-button {
                    margin-bottom: 0;
                }
            }

            > .overflow-menu-button {
                ${RemoveDefaultButtonStyles};

                &:active,
                &:focus {
                    color: ${Colours.BLUEBERRY_DARKEST};
                }
            }
        }
    }
`;

function isTouchDevice() {
    return !!(('ontouchstart' in window)
        || (navigator.maxTouchPoints > 0)
        || (navigator.msMaxTouchPoints > 0));
}

const SUPPORTED_BUSINESS_UNITS = {
    MORTGAGES: 'mortgages',
    INSURANCE: 'insurance',
    CREDIT_CARDS: 'credit-cards',
    LOANS: 'loans',
    BANKING: 'banking',
    INVESTING: 'investing',
};

const TABS = [
    {
        title: BusinessUnitsMessages[SUPPORTED_BUSINESS_UNITS.MORTGAGES],
        render: ({ initialMortgageRates }) => (
            <HeroTabContentMortgages initialMortgageRates={initialMortgageRates} />
        ),
    },
    {
        title: BusinessUnitsMessages[SUPPORTED_BUSINESS_UNITS.INSURANCE],
        render: ({ homeInsuranceProviderRedirects }) => (
            <HeroTabContentInsurance homeInsuranceProviderRedirects={homeInsuranceProviderRedirects} />
        ),
    },
    {
        title: BusinessUnitsMessages[SUPPORTED_BUSINESS_UNITS.CREDIT_CARDS],
        render: () => <HeroTabContentCreditCards />,
    },
    {
        title: BusinessUnitsMessages[SUPPORTED_BUSINESS_UNITS.LOANS],
        render: () => <HeroTabContentLoans />,
    },
    {
        title: BusinessUnitsMessages[SUPPORTED_BUSINESS_UNITS.BANKING],
        render: () => <HeroTabContentBanking />,
    },
    {
        title: BusinessUnitsMessages[SUPPORTED_BUSINESS_UNITS.INVESTING],
        render: () => <HeroTabContentInvesting />,
    },
];

HeroTabContainer.blockKey = 'rh/hero-tab-container';
HeroTabContainer.requiresLayoutRow = true;
HeroTabContainer.supportedPageTypes = [
    PAGE_TEMPLATES.corporate,
    PAGE_TEMPLATES.sandbox,
];

export default HeroTabContainer;
